/// <amd-module name="Search/Package/Components/SearchResultsList" />

import * as React from "react";
import * as Constants from "Search/Package/Constants";
import { isFeatureFlagEnabled } from "VSS/Platform/Feature";
import { ActionCreator } from "Search/Package/Flux/ActionCreator";
import { IVssPageContext } from "VSS/Platform/Context";
import { ISearchStoreState } from "Search/Shared/Base/Stores/SearchStore2";
import { PackageSearchRequest, PackageSearchResponseContent, PackageResult } from "Search/WebApi/Contracts/Search.Shared";
import { ItemsList, IItemsListProps } from "Search/Package/Components/ItemsList";
import { SearchStatus } from "Search/Shared/Base/Stores/SearchStore";
import { Intersection } from "VSSUI/Intersection";
import { ShowMoreRow } from "Search/Shared/Components/ShowMoreRow";
import { LoadMore } from "Search/Shared/Components/LoadMore";

export interface SearchResultsListProps {
    pageContext: IVssPageContext;

    actionCreator: ActionCreator;

    searchStoreState: ISearchStoreState<PackageSearchRequest, PackageSearchResponseContent>;

    isHosted: boolean;
}

interface IScrollableItemsListProps extends IItemsListProps {
    infiniteScroll: boolean;

    moreResultsAvailable: boolean;

    fetchMoreResults: (newSkip: number, newTake: number) => void;

    query: PackageSearchRequest;
}

export const SearchResultsList = (props: SearchResultsListProps) => {
    const {
        query,
        response,
        searchStatus,
        fetchMoreScenario
    } = props.searchStoreState;

    const {
        fetchMoreResults,
        notifyResultsRendered,
        clickPackageLink
    } = props.actionCreator;

    const items = response ? response.results : [];
    const totalResultsCount = response ? response.count : 0;
    const searchSucceeded = searchStatus === SearchStatus.Success;
    const needShowMore = response
        ? (Math.min(totalResultsCount, Constants.PackageSearchMaxTakeResults) > (query.$skip + query.$top))
        : false;

    const infiniteScroll = isFeatureFlagEnabled(props.pageContext, Constants.FeatureFlagConstants.InfiniteScroll, false);

    // Add an empty row to the items to render spinner while fetching the results in case of infinite scroll.
    const newItems = infiniteScroll && needShowMore ? [...items, {} as PackageResult] : items;

    if (searchSucceeded || fetchMoreScenario) {
        return (
            <ScrollableItemsList
                items={newItems}
                {...props}
                itemsLoading={searchStatus === SearchStatus.Loading}
                notifyOnRendered={notifyResultsRendered}
                onPackageItemClicked={clickPackageLink}
                renderLoadingRow={() => getLoadMoreRow(searchStatus === SearchStatus.Failed)}
                infiniteScroll={infiniteScroll}
                moreResultsAvailable={needShowMore}
                fetchMoreResults={fetchMoreResults}
                query={query} />
        );
    }

    return null;
}

export class ScrollableItemsList extends React.Component<IScrollableItemsListProps, {}> {
    private listRef: React.RefObject<HTMLDivElement | null> = React.createRef<HTMLDivElement>();
    public render(): JSX.Element {
        const { items, itemsLoading, infiniteScroll, fetchMoreResults, moreResultsAvailable, query } = this.props;
        return (
            <Intersection>
                <div
                    // @ts-ignore TypeScript 3.5 update
                    ref={this.listRef}
                    className="search-results-container v-scroll-auto h-scroll-hidden">
                    {
                        items.length > 0 &&
                        <ItemsList {...this.props} />
                    }
                    {
                        !infiniteScroll &&
                        <ShowMoreRow
                            className="package-result"
                            onClick={() => fetchMoreResults(query.$top, Constants.PackageSearchMaxTakeResults)}
                            needShowMore={moreResultsAvailable}
                            itemsLoading={itemsLoading} />
                    }
                </div>
            </Intersection>
        );
    }

    public componentDidMount(): void {
        if (this.listRef.current) {
            this.listRef.current.addEventListener("scroll", this.fetchMoreIfNeeded);
        }
    }

    public componentWillUnmount(): void {
        if (this.listRef.current) {
            this.listRef.current.removeEventListener("scroll", this.fetchMoreIfNeeded);
        }
    }

    private fetchMoreIfNeeded = (): void => {
        const { query, fetchMoreResults, infiniteScroll, moreResultsAvailable } = this.props;
        if (this.listRef && this.listRef.current && infiniteScroll && moreResultsAvailable) {
            const viewPortBottom = this.listRef.current.clientHeight + this.listRef.current.scrollTop;
            const totalHeight = this.listRef.current.scrollHeight;

            if (totalHeight - viewPortBottom < 150) {
                const newTake = Math.min(query.$top + Constants.PackageSearchLoadMoreTakeResults, Constants.PackageSearchMaxTakeResults);
                fetchMoreResults(query.$skip, newTake);
            }
        }
    }
}

function getLoadMoreRow(loadMoreFailed: boolean): JSX.Element {
    return <LoadMore
        error={loadMoreFailed} />;
}